import React from 'react';
import { useFxRate } from '../context/FxRateContext';
import { formatDualPHP, formatDualUSD, formatPHP, formatUSD } from '../utils/money';

/**
 * Drop-in component that renders a price with both PHP and USD shown.
 *
 *   <DualPrice php={4500} />           // → ₱4,500 ($73.21)
 *   <DualPrice usd={500} />            // → $500.00 (₱30,837)
 *   <DualPrice php={199} compact />    // → ₱199 · $3.24
 *   <DualPrice php={4500} usdClassName="text-[10px] text-[#5C7599]" />
 *
 * Props:
 *   php          number — primary amount in pesos
 *   usd          number — primary amount in USD (use when listing is USD-priced)
 *   compact      bool — use middot separator (no parens)
 *   showUsd      bool (default true)
 *   showPhp      bool (default true) — only relevant when usd= is provided
 *   phpDecimals  number — force decimal places for the PHP part
 *   className    string — applied to the wrapper span
 *   usdClassName string — applied to the secondary-currency inline span
 */
export function DualPrice({
  php,
  usd,
  compact = false,
  showUsd = true,
  showPhp = true,
  phpDecimals,
  className = '',
  usdClassName = '',
}) {
  const { phpUsdRate, usdPhpRate } = useFxRate();

  if (usd != null) {
    if (!showPhp || !phpUsdRate) {
      return <span className={className}>{formatUSD(usd)}</span>;
    }
    // Format the two parts so we can apply a separate class to the secondary
    const main = formatUSD(usd);
    const phpAmt = usdPhpRate > 0 ? usd * usdPhpRate : (phpUsdRate > 0 ? usd / phpUsdRate : null);
    if (phpAmt == null) return <span className={className}>{main}</span>;
    const sep = compact ? ' · ' : ' (';
    const close = compact ? '' : ')';
    return (
      <span className={className}>
        {main}
        <span className={usdClassName || undefined}>
          {sep}{formatPHP(phpAmt, { minFractionDigits: 0, maxFractionDigits: 0 })}{close}
        </span>
      </span>
    );
  }

  const main = formatPHP(php, { minFractionDigits: phpDecimals, maxFractionDigits: phpDecimals });
  if (!showUsd || !phpUsdRate) {
    return <span className={className}>{main}</span>;
  }
  const usdAmt = phpUsdRate > 0 ? Number(php) * phpUsdRate : null;
  if (usdAmt == null || !Number.isFinite(usdAmt)) {
    return <span className={className}>{main}</span>;
  }
  const sep = compact ? ' · ' : ' (';
  const close = compact ? '' : ')';
  return (
    <span className={className} data-testid="dual-price">
      {main}
      <span className={usdClassName || undefined}>
        {sep}{formatUSD(usdAmt)}{close}
      </span>
    </span>
  );
}

/**
 * Convenience hook variant: returns the formatted string. Use when you need
 * a plain string (e.g. inside template literals or as a non-React label).
 *
 *   const label = useDualPrice(4500); // → "₱4,500 ($73.21)"
 */
export function useDualPrice(php, opts = {}) {
  const { phpUsdRate } = useFxRate();
  return formatDualPHP(php, { phpUsdRate, ...opts });
}

export function useDualPriceUSD(usd, opts = {}) {
  const { phpUsdRate, usdPhpRate } = useFxRate();
  return formatDualUSD(usd, { phpUsdRate, usdPhpRate, ...opts });
}

export default DualPrice;
